import { Input, Pagination, Spin, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { initializeCreateDrawer } from './actions'

import { getDrawerTitle, submitDrawerForm } from './helpers'
import { extractBayConfigurations } from './utils'
import { getVesselColumns } from './tableColumns'

import Card from 'components/Card'
import Heading from 'components/Heading/Heading'
import VesselDrawer from './components/VesselDrawer'

import '../../global/styles/global.less'
import './Admin.less'

const { Search } = Input

const Admin = ({
  exportVessel,
  onSaveVesselDetails,
  pagination,
  requestVessel,
  requestingVessels,
  requestingVesselDetails,
  savingVesselDetails,
  searchQuery,
  setVesselPage,
  setVesselSearchQuery,
  vesselDetails,
  vessels,
}) => {
  const { t } = useTranslation()

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [drawerType, setDrawerType] = useState(null)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [drawerInitialConfig, setDrawerInitialConfig] = useState(null)
  const [pendingUpdateRecord, setPendingUpdateRecord] = useState(null)
  const [pendingCloneRecord, setPendingCloneRecord] = useState(null)

  // Watch for vessel details to be loaded after requestVessel is called
  useEffect(() => {
    if (pendingUpdateRecord && vesselDetails && !requestingVesselDetails) {
      const bayConfig = extractBayConfigurations(vesselDetails)

      setDrawerInitialConfig(bayConfig)
      setDrawerType('update')
      setSelectedRecord(vesselDetails)
      setDrawerVisible(true)
      setPendingUpdateRecord(null)
    }
  }, [vesselDetails, requestingVesselDetails, pendingUpdateRecord])

  // Watch for vessel details to be loaded for clone operation
  useEffect(() => {
    if (pendingCloneRecord && vesselDetails && !requestingVesselDetails) {
      const bayConfig = extractBayConfigurations(vesselDetails)

      setDrawerInitialConfig(bayConfig)
      setDrawerType('clone')
      setSelectedRecord({
        ...vesselDetails,
        abbreviation: '',
        imoNumber: '',
        vesselAbbreviation: '',
        vesselName: '',
      })
      setDrawerVisible(true)
      setPendingCloneRecord(null)
    }
  }, [vesselDetails, requestingVesselDetails, pendingCloneRecord])

  // Search handler — filters locally from cached allVessels in Redux
  const handleSearch = value => {
    setVesselPage(1) // Reset to first page on new search
    setVesselSearchQuery(value)
  }

  // Pagination handler — no API call, data is already cached in Redux (allVessels)
  const handlePaginationChange = page => {
    setVesselPage(page)
  }

  const handleClone = record => {
    // Clear search field when cloning
    setVesselSearchQuery('')
    // Don't open drawer yet - show page loading overlay first
    setPendingCloneRecord(record)
    requestVessel(record.id)
  }

  // Vessel CRUD Handlers

  const handleCreate = () => {
    // Clear search field when creating
    setVesselSearchQuery('')
    initializeCreateDrawer({
      setDrawerType,
      setDrawerVisible,
      setSelectedRecord,
    })
  }

  const handleUpdate = record => {
    // Clear search field when updating
    setVesselSearchQuery('')
    // Don't open drawer yet - show page loading overlay first
    setPendingUpdateRecord(record)
    requestVessel(record.id)
  }

  const handleExport = record => {
    exportVessel(record.id)
  }

  // Drawer Handlers

  const handleDrawerClose = () => {
    setDrawerVisible(false)
    setDrawerType(null)
    setSelectedRecord(null)
    setDrawerInitialConfig(null)
  }

  const handleDrawerSubmit = (payload, onError) => {
    const result = submitDrawerForm({
      ...payload,
      drawerType,
      onError,
      onSaveVesselDetails,
      onSuccess: handleDrawerClose,
      selectedRecord,
    })

    // If validation failed, throw error
    if (!result.success) {
      throw new Error(result.error || 'Validation failed')
    }

    // Note: Drawer will close via onSuccess callback after saga completes
  }

  // Table Column Configuration

  const vesselColumns = getVesselColumns({
    handleClone,
    handleExport,
    handleUpdate,
    t,
  })

  // Render

  return (
    <div className="page__container">
      <Heading level={2}>{t('admin.title')}</Heading>
      {/* Page-level loading overlay when fetching or saving vessel details */}
      {((requestingVesselDetails &&
        (pendingUpdateRecord || pendingCloneRecord)) ||
        savingVesselDetails) && (
        <div className="admin-page-loading-overlay">
          <Spin />
        </div>
      )}

      {/* Main Vessel Table */}

      <Card
        className="admin__card"
        title={t('admin.vesselDetails')}
        headerActionButton={{
          label: t('admin.tableColumns.create'),
          onClick: handleCreate,
        }}
      >
        <div className="vessel-management__search">
          <Search
            allowClear
            enterButton
            onSearch={handleSearch}
            placeholder={t('admin.searchPlaceholder')}
            style={{ marginBottom: 16, maxWidth: 400 }}
            value={searchQuery}
            onChange={e => {
              if (isEmpty(e.target.value)) {
                setVesselPage(1)
                setVesselSearchQuery('')
              } else {
                setVesselPage(1) // Reset to page 1 when typing a new search
                setVesselSearchQuery(e.target.value)
              }
            }}
          />
        </div>

        <Table
          columns={vesselColumns}
          dataSource={vessels}
          loading={requestingVessels}
          pagination={false}
          rowKey="imoNumber"
        />

        {pagination.total > 0 && (
          <Pagination
            className="vessel-management__pagination"
            current={pagination.page}
            onChange={handlePaginationChange}
            pageSize={pagination.pageSize}
            showSizeChanger={false}
            showTitle={false}
            style={{ marginTop: 16, textAlign: 'right' }}
            total={pagination.total}
          />
        )}
      </Card>

      {/* Vessel Details Drawer */}
      {(drawerVisible || drawerType) && (
        <VesselDrawer
          drawerTitle={getDrawerTitle(drawerType, t)}
          drawerType={drawerType}
          drawerVisible={drawerVisible}
          handleDrawerClose={handleDrawerClose}
          handleDrawerSubmit={handleDrawerSubmit}
          initialConfig={drawerInitialConfig}
          isLoading={requestingVesselDetails}
          isSaving={savingVesselDetails}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
        />
      )}
    </div>
  )
}

Admin.propTypes = {
  exportVessel: PropTypes.func.isRequired,
  onSaveVesselDetails: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  requestVessel: PropTypes.func.isRequired,
  requestVessels: PropTypes.func.isRequired,
  requestingVesselDetails: PropTypes.bool,
  requestingVessels: PropTypes.bool.isRequired,
  savingVesselDetails: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setVesselPage: PropTypes.func.isRequired,
  setVesselSearchQuery: PropTypes.func.isRequired,
  vesselDetails: PropTypes.object,
  vessels: PropTypes.array.isRequired,
}

export default Admin
